/**
 * §2.9 MP4 fallback: a single module-level <video> element shared by all cards. Only one card is
 * scrubbing at a time, so only one fallback video ever exists.
 */
class ScrubVideoControllerImpl {
  private video: HTMLVideoElement | null = null;
  private host: HTMLElement | null = null;
  private durationMs = 0;
  private pendingXr: number | null = null;
  private raf = 0;

  private ensure(): HTMLVideoElement {
    if (!this.video) {
      const v = document.createElement('video');
      v.muted = true;
      v.playsInline = true;
      v.preload = 'metadata';
      v.className = 'card-scrub-video';
      v.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;pointer-events:none;background:#000';
      this.video = v;
    }
    return this.video;
  }

  attach(host: HTMLElement, src: string, durationMs: number): void {
    const v = this.ensure();
    if (this.host && this.host !== host) this.detach();
    this.host = host;
    this.durationMs = durationMs;
    if (v.getAttribute('src') !== src) {
      v.src = src;
      v.load();
    }
    if (v.parentElement !== host) host.appendChild(v);
  }

  seek(xr: number): void {
    this.pendingXr = xr;
    if (this.raf) return;
    this.raf = requestAnimationFrame(() => {
      this.raf = 0;
      const v = this.video;
      if (!v || this.pendingXr == null || !this.host) return;
      if (v.seeking) {
        // skip while seeking; latest xr will be applied on the next move
        return;
      }
      const dur = Number.isFinite(v.duration) && v.duration > 0 ? v.duration : this.durationMs / 1000;
      if (dur > 0) v.currentTime = Math.min(dur - 0.05, Math.max(0, this.pendingXr * dur));
      this.pendingXr = null;
    });
  }

  detach(host?: HTMLElement): void {
    if (host && this.host !== host) return;
    const v = this.video;
    if (v?.parentElement) v.parentElement.removeChild(v);
    this.host = null;
    this.pendingXr = null;
  }

  isAttachedTo(host: HTMLElement): boolean {
    return this.host === host;
  }
}

export const ScrubVideoController = new ScrubVideoControllerImpl();
